
import { Component, Vue } from 'vue-property-decorator';
import { ContentLoader } from 'vue-content-loader';
import { AccountInfo } from '@airgap/beacon-sdk';
import { getTokenInfoPath, getTokenInfoContract } from '@/lib/requests-helper';
import { Token, ContractToken, TradeFromTzstats } from '@/lib/types-helper';
import { Module1Module } from '@/store/modules/module1';

@Component({
  components: {
    ContentLoader
  }
})
export default class Propose extends Vue {
  mutez_amount1 = 0;
  mutez_amount2 = 0;
  tokens1: Token[] = [];
  tokens2: Token[] = [];
  yourTradeId = 0;

  showModal = false;
  allowModalToBeClosed = false;
  modalTitle = 'Please Wait';
  modalParagraph = 'Transaction pending...'

  get account () : AccountInfo | undefined | null {
    return this.$store.state.module1.account;
  }

  get allContractTradeData () : TradeFromTzstats[] {
    return this.$store.state.module1.allContractTradeData;
  }

  get myContractTradeData () : TradeFromTzstats[] {
    return this.$store.state.module1.myContractTradeData;
  }

  get isDev () : boolean {
    return Module1Module.isDev;
  }

  get net () : string {
    return Module1Module.net;
  }

  get contractAddress () : string {
    return Module1Module.contractAddress;
  }

  tezosToMutez (tezos: number) : number {
    return tezos * 1000000;
  }

  getTotalTokensFrom (tokens: any[]) : number {
    let total = 0;
    tokens.forEach(token => {
      total += token.amount;
    });
    return total;
  }

  possiblyCloseModal () : void {
    if (this.allowModalToBeClosed) {
      this.showModal = false;
      this.allowModalToBeClosed = false;
    }
  }

  getImageURLFromDisplayURI (uri: string) : string {
    let newUri = uri;

    if (uri.includes('ipfs://')) {
      newUri = uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }

    if (uri.includes('ar://')) {
      newUri = uri.replace('ar://', 'https://arweave.new/');
    }

    return newUri;
  }

  /*
   * Examples of URLs I can parse from Objkt:
   *   https://objkt.com/asset/KT19QrZvpKD2XFkd4iqisXmf8iUSmuJrKZPd/97
   *   https://objkt.com/asset/hollows/186
   */
  async addHave () : Promise<void> {
    try {
      const objktURL = (this.$refs.haveObjktURL as HTMLInputElement).value;
      (this.$refs.haveObjktURL as HTMLInputElement).value = '';

      const fxHashURL = (this.$refs.haveFXURL as HTMLInputElement).value;
      (this.$refs.haveFXURL as HTMLInputElement).value = '';

      const amount = (this.$refs.haveAmount as HTMLInputElement).value;
      (this.$refs.haveAmount as HTMLInputElement).value = '1';

      const isObjkt = objktURL.includes('objkt.com');
      const isFXHash = fxHashURL.includes('fxhash.xyz/gentk');

      let id: string | undefined;
      let collection: string | undefined;

      if (isObjkt) {
        const split = objktURL.split('asset/');
        const parts = split[split.length - 1].split('/');
        collection = parts[0];
        id = parts[1];
      }

      if (isFXHash) {
        const split = fxHashURL.split('gentk/');
        id = split[split.length - 1];

        if (parseInt(id) > 589146) {
          collection = 'fxhash';
        } else {
          collection = 'fxhashgenesis';
        }
      }

      if (!collection || !id) {
        throw new Error('URL not recognized');
      }

      let info;
      if (collection.substring(0, 3) === 'KT1') {
        info = await getTokenInfoContract(id, collection);
      } else {
        info = await getTokenInfoPath(id, collection);
      }

      const royalty_addresses : string[] = [];
      info.royalties.forEach((royaltyObj: {amount: number, decimals: number, receiver_address: string}) => {
        royalty_addresses.push(royaltyObj.receiver_address);
      });

      this.tokens1.push({
        id: parseInt(info.token_id),
        amount: parseInt(amount),
        fa2: info.fa.contract,
        name: info.name,
        image: this.getImageURLFromDisplayURI(info.display_uri),
        royalty_addresses
      });
    } catch (error: any) {
      console.warn(error);

      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">' + 'Bad URL' + '</span>';
      this.modalParagraph = 'Could not get data for that token from fx(hash) or Objkt. Please check for issues with your URL and try again.';
      this.allowModalToBeClosed = true;
    }
  }

  /*
   * Examples of URLs I can parse from Objkt:
   *   https://objkt.com/asset/KT19QrZvpKD2XFkd4iqisXmf8iUSmuJrKZPd/97
   *   https://objkt.com/asset/hollows/186
   */
  async addWant () : Promise<void> {
    try {
      const objktURL = (this.$refs.wantObjktURL as HTMLInputElement).value;
      (this.$refs.wantObjktURL as HTMLInputElement).value = '';

      const fxHashURL = (this.$refs.wantFXURL as HTMLInputElement).value;
      (this.$refs.wantFXURL as HTMLInputElement).value = '';

      const amount = (this.$refs.wantAmount as HTMLInputElement).value;
      (this.$refs.wantAmount as HTMLInputElement).value = '1';

      const isObjkt = objktURL.includes('objkt.com');
      const isFXHash = fxHashURL.includes('fxhash.xyz/gentk');

      let id: string | undefined;
      let collection: string | undefined;

      if (isObjkt) {
        const split = objktURL.split('asset/');
        const parts = split[split.length - 1].split('/');
        collection = parts[0];
        id = parts[1];
      }

      if (isFXHash) {
        const split = fxHashURL.split('gentk/');
        id = split[split.length - 1];
        collection = 'fxhash';
      }

      if (!collection || !id) {
        throw new Error('URL not recognized');
      }

      let info;
      if (collection.substring(0, 3) === 'KT1') {
        info = await getTokenInfoContract(id, collection);
      } else {
        info = await getTokenInfoPath(id, collection);
      }

      const royalty_addresses : string[] = [];
      info.royalties.forEach((royaltyObj: {amount: number, decimals: number, receiver_address: string}) => {
        royalty_addresses.push(royaltyObj.receiver_address);
      });

      this.tokens2.push({
        id: parseInt(info.token_id),
        amount: parseInt(amount),
        fa2: info.fa.contract,
        name: info.name,
        image: this.getImageURLFromDisplayURI(info.display_uri),
        royalty_addresses
      });
    } catch (error: any) {
      console.warn(error);

      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">' + 'Bad URL' + '</span>';
      this.modalParagraph = 'Could not get data for that token from Objkt. Please check for issues with your URL and try again.';
      this.allowModalToBeClosed = true;
    }
  }

  changeTezosHave () : void {
    let tez = parseFloat((this.$refs.haveMutez as HTMLInputElement).value);
    if (tez < 0 || !tez) {
      tez = 0;
    }
    this.mutez_amount1 = tez;
  }

  changeTezosWant () : void {
    let tez = parseFloat((this.$refs.wantMutez as HTMLInputElement).value);
    if (tez < 0 || !tez) {
      tez = 0;
    }
    this.mutez_amount2 = tez;
  }

  deleteHaveNFT (index: number) : void {
    this.tokens1.splice(index, 1);
  }

  deleteWantNFT (index: number) : void {
    this.tokens2.splice(index, 1);
  }

  async login () : Promise<void> {
    try {
      this.showModal = true;
      this.modalTitle = 'Logging In';
      this.modalParagraph = 'Please wait...';

      await Module1Module.Login();

      this.showModal = false;
    } catch (error: any) {
      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">Error: ' + error.title + '</span>';
      this.modalParagraph = error.description;

      if (!error.title && !error.description) {
        this.modalTitle = '<span class="text-red-600">Error</span>';
        this.modalParagraph = 'If this was unexpected please contact support';
      }

      this.allowModalToBeClosed = true;
      console.warn(error);
    }
  }

  async proposeTrade () : Promise<void> {
    if (!this.$store.state.module1.account) {
      await this.login();
    }

    if (!this.$store.state.module1.account) {
      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">' + 'Please Login First' + '</span>';
      this.modalParagraph = 'Once logged in you can propose trades';
      this.allowModalToBeClosed = true;
      return;
    }

    if (!this.tokens1.length || !this.tokens2.length) {
      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">' + 'Invalid trade' + '</span>';
      this.modalParagraph = 'Both sides must contain FA2 compliant tokens or NFTs';
      this.allowModalToBeClosed = true;
      return;
    }

    try {
      this.showModal = true;
      this.modalTitle = 'Creating Trade';
      this.modalParagraph = 'Please confirm the transaction...';

      if (this.$store.state.module1.xtznftswapContract && this.$store.state.module1.account) {
        const tokens1: ContractToken[] = [];
        this.tokens1.forEach((token: Token) => {
          tokens1.push({
            fa2: token.fa2,
            id: token.id,
            amount: token.amount,
            royalty_addresses: token.royalty_addresses
          });
        });

        const tokens2: ContractToken[] = [];
        this.tokens2.forEach((token: Token) => {
          tokens2.push({
            fa2: token.fa2,
            id: token.id,
            amount: token.amount,
            royalty_addresses: token.royalty_addresses
          });
        });

        let trade_proposal = {
          proposer: this.$store.state.module1.account.address,
          acceptor: this.contractAddress,
          mutez_amount1: this.tezosToMutez(this.mutez_amount1),
          mutez_amount2: this.tezosToMutez(this.mutez_amount2),
          tokens1,
          tokens2
        };

        // TEST TRADE HARDCODED NFTS ON DEV
        if (this.isDev) {
          console.log('using hardcoded test trade');
          this.mutez_amount1 = 1;
          this.mutez_amount2 = 1;
          trade_proposal = {
            proposer: this.$store.state.module1.account.address,
            acceptor: this.$store.state.module1.xtznftswapContract.address, // ANON TRADE
            mutez_amount1: this.tezosToMutez(1),
            mutez_amount2: this.tezosToMutez(1),
            tokens1: [
              {
                fa2: 'KT1KdrJroMbVfgQzhNSzFFtCCgB9yBm51ynG',
                id: 2,
                amount: 1,
                royalty_addresses: []
              }
            ],
            tokens2: [
              {
                fa2: 'KT1KdrJroMbVfgQzhNSzFFtCCgB9yBm51ynG',
                id: 1,
                amount: 1,
                royalty_addresses: []
              }
            ]
          };

          console.log(trade_proposal);
        }

        const batch = await Module1Module.tezosToolkitInstance.wallet.batch();

        for (let i = 0; i < trade_proposal.tokens1.length; i++) {
          const token = trade_proposal.tokens1[i];
          const fa2Contract = await Module1Module.tezosToolkitInstance.wallet.at(token.fa2);

          batch.withContractCall(
            fa2Contract.methods.update_operators([{
              add_operator: {
                owner: this.$store.state.module1.account.address,
                operator: this.$store.state.module1.xtznftswapContract.address,
                token_id: token.id
              }
            }])
          );
        }

        batch.withTransfer({
          ...this.$store.state.module1.xtznftswapContract.methodsObject
            .propose_trade(trade_proposal)
            .toTransferParams({
              amount: this.mutez_amount1 // in tez
            })
        });

        const batchOp = await batch.send();

        this.showModal = true;
        this.modalTitle = 'Confirming';
        this.modalParagraph = 'Trade proposal is confirming please wait...<br><br>This can a few minutes so please do not reload or leave the page.';

        // alters the batchOp object to have operationResults() when successful
        await batchOp.confirmation();

        const opResults = await batchOp.operationResults();
        const hash = batchOp.opHash;

        // highly doubt i can make this typesafe
        const subevent: any = opResults[opResults.length - 1];
        this.yourTradeId = parseInt(subevent.metadata.operation_result.lazy_storage_diff[0].diff.updates[0].key.int);

        this.modalTitle = 'Success!';
        this.modalParagraph = `You've created <a href="https://tzkt.io/${hash}" target="_blank">trade ID #` + this.yourTradeId + ` on chain</a>. Its status can be checked anytime in the My Trade tab.<br><br>Share this trade with anyone via <a href="https://xtznftswap.xyz/?trade=${this.yourTradeId}#/accept" target="_blank">this permalink</a>!`;
        this.allowModalToBeClosed = true;
      }
    } catch (error: any) {
      this.modalTitle = '<span class="text-red-600">Error: ' + error.title + '</span>';
      this.modalParagraph = error.description;
      this.allowModalToBeClosed = true;
      console.warn(error);
    }
  }
}
